/**
 * HighVolRegimeCard — compact index card
 */
import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { TrendingUp, TrendingDown, Warning, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const REGIME_CFG = {
  NORMAL:   { color: '#10b981', bg: '#f0fdf4', Icon: CheckCircle },
  CAUTION:  { color: '#f59e0b', bg: '#fffbeb', Icon: Warning },
  HIGH_VOL: { color: '#ef4444', bg: '#fef2f2', Icon: ErrorIcon },
};
const ACTION_CFG = {
  HOLD:        { color: '#10b981' },
  REDUCE_70:   { color: '#f59e0b' },
  REDUCE_40:   { color: '#ea580c' },
  NO_NEW_BUY:  { color: '#ef4444' },
  REENTER_OK:  { color: '#3b82f6' },
};

const HighVolRegimeCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const regime = REGIME_CFG[data.regime] || REGIME_CFG.NORMAL;
  const actionCfg = ACTION_CFG[data.action] || ACTION_CFG.HOLD;
  const score = data.score || 0;
  const scoreClr = score > 50 ? '#ef4444' : score > 30 ? '#f59e0b' : '#10b981';
  const riskLabel = score > 50
    ? t('highVolRegime.riskLevel.veryHigh', 'Rất cao')
    : score > 30
    ? t('highVolRegime.riskLevel.high', 'Cao')
    : score > 15
    ? t('highVolRegime.riskLevel.moderate', 'Trung bình')
    : t('highVolRegime.riskLevel.low', 'Thấp');

  const MetricCell = ({ label, value, warn }) => (
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.3px' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: warn ? '#ef4444' : '#1e293b', fontFamily: 'monospace', mt: 0.1 }}>{value}</Typography>
    </Box>
  );

  const atrLabel = data.atr_ratio > 1.5
    ? t('highVolRegime.metrics.veryHigh', 'Rất cao')
    : data.atr_ratio > 1.2
    ? t('highVolRegime.metrics.high', 'Cao')
    : t('highVolRegime.metrics.normal', 'Bình thường');

  return (
    <Box sx={{ borderRadius: 1.5, border: `1px solid ${alpha(regime.color, 0.2)}`, bgcolor: '#fff', overflow: 'hidden', transition: 'all 0.2s', '&:hover': { borderColor: alpha(regime.color, 0.35), boxShadow: `0 4px 16px ${alpha(regime.color, 0.08)}` } }}>
      {/* Top color bar */}
      <Box sx={{ height: 3, bgcolor: regime.color }} />

      <Box sx={{ p: 1.5 }}>
        {/* Header: index name + regime badge */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>{data.index_code}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: 1.5, bgcolor: regime.bg }}>
            <regime.Icon sx={{ fontSize: 14, color: regime.color }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: regime.color }}>
              {t(`highVolRegime.regimes.${data.regime}.label`, data.regime)}
            </Typography>
          </Box>
        </Box>

        {/* Score + Action */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.3px' }}>
              {t('highVolRegime.riskScore', 'Điểm rủi ro')}
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: scoreClr, mt: 0.2 }}>{riskLabel}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.3px' }}>
              {t('highVolRegime.recommendedAction', 'Action')}
            </Typography>
            <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: actionCfg.color, mt: 0.2 }}>
              {t(`highVolRegime.actions.${data.action}`, data.action)}
            </Typography>
          </Box>
        </Box>

        {/* Metrics grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, mb: 1.2, pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <MetricCell label={t('highVolRegime.metrics.volatility', 'Biến động')} value={atrLabel} warn={data.atr_ratio > 1.2} />
          <MetricCell label={t('highVolRegime.metrics.declineSessions', 'Phiên giảm mạnh')} value={`${data.wrdd_count_5d || 0} ${t('highVolRegime.metrics.sessions', 'phiên')}`} warn={data.wrdd_count_5d > 0} />
          <MetricCell label={t('highVolRegime.metrics.distributionDays', 'Ngày phân phối')} value={`${data.dist_count_20d || 0} / 20 ${t('highVolRegime.metrics.days', 'ngày')}`} warn={data.dist_count_20d >= 4} />
          <MetricCell label={t('highVolRegime.metrics.warningSignals', 'Tín hiệu cảnh báo')} value={`${data.trigger_count || 0} / 6`} warn={data.trigger_count >= 3} />
        </Box>

        {/* Breadth */}
        {data.breadth && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 0.8, borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{t('highVolRegime.marketBreadth', 'Breadth')}</Typography>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: '#3b82f6', fontFamily: 'monospace' }}>
                {(data.breadth.breadth * 100).toFixed(1)}%
              </Typography>
              {data.breadth.breadth_weak && (
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#f59e0b', px: 0.5, py: 0.1, bgcolor: '#fffbeb', borderRadius: 0.8 }}>{t('highVolRegime.breadthWeak', 'WEAK')}</Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <TrendingUp sx={{ fontSize: 13, color: '#10b981' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>{data.breadth.advancers}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <TrendingDown sx={{ fontSize: 13, color: '#ef4444' }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>{data.breadth.decliners}</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Derivatives */}
        {data.derivatives && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 0.8, borderBottom: '1px solid #f1f5f9' }}>
            <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>VN30F</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{
                fontSize: '0.68rem', fontWeight: 700, px: 0.6, py: 0.1, borderRadius: 0.8,
                bgcolor: data.derivatives.deriv_risk_signal === 'BEARISH' ? '#fef2f2' : data.derivatives.deriv_risk_signal === 'BULLISH' ? '#f0fdf4' : '#f8fafc',
                color: data.derivatives.deriv_risk_signal === 'BEARISH' ? '#ef4444' : data.derivatives.deriv_risk_signal === 'BULLISH' ? '#10b981' : '#64748b',
              }}>
                {data.derivatives.deriv_risk_signal || 'NEUTRAL'}
              </Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                {t('highVolRegime.basis', 'Basis')} <Typography component="span" sx={{ fontWeight: 700, fontFamily: 'monospace', color: '#64748b' }}>{data.derivatives.basis_pct?.toFixed(3)}%</Typography>
              </Typography>
            </Box>
          </Box>
        )}

        {/* Triggers */}
        {data.reasons?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {data.reasons.map((r, i) => (
              <Typography key={i} sx={{
                fontSize: '0.62rem', fontWeight: 600, px: 0.7, py: 0.2, borderRadius: 1,
                bgcolor: alpha('#8b5cf6', 0.06), color: '#7c3aed', whiteSpace: 'nowrap',
              }}>
                {r}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default HighVolRegimeCard;
